"use client"

// components/v3/site-chrome.tsx — shared chrome for v3 pages.
//
// Register (方向 C 雙域收斂): the chrome lives in the PAPER register — warm
// paper, ink, 朱砂 vermilion. Instrument moments (diagnostic, ledger, metric
// walls) are dark inset rooms rendered by page sections, not by the chrome.
//
// Nav policy (V3-MASTER-SPEC §2.0): ≤6 items; only live routes render.
// CTA discipline (§2.0): exactly ONE CTA group per page (<CtaBlock/>).

import Link from "next/link"
import type { ReactNode } from "react"
import { motion, useReducedMotion } from "framer-motion"
import { cta, footerLine, identity } from "@/lib/proto/content"
import { FadeUp, ZH_SERIF, Eyebrow, paper, type } from "./primitives"

const ease = [0.16, 1, 0.3, 1] as const

// Live v3 routes only — extend as W2–W4 pages land.
const NAV = [
  { href: "/brand-engineering", zh: "品牌工程", en: "Brand Engineering" },
  { href: "/about", zh: "關於", en: "About" },
  { href: "/work-with-me", zh: "合作", en: "Work with me" },
]

export function SiteHeader({ current }: { current?: string }) {
  const reduce = useReducedMotion()
  return (
    <header
      className="fixed inset-x-0 top-0 z-40 backdrop-blur-md"
      style={{ background: "rgba(242,236,223,0.88)", borderBottom: `1px solid ${paper.hairlineSoft}` }}
    >
      <motion.div
        initial={reduce ? false : { opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease }}
        className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 md:px-10"
      >
        <Link href="/" className="focus-visible:outline-none">
          <span
            className="text-sm tracking-[0.04em]"
            style={{ fontFamily: "var(--proto-fraunces)", color: paper.ink }}
          >
            {identity.name}
          </span>
        </Link>
        <nav aria-label="主導航" className="hidden items-center gap-8 md:flex">
          {NAV.map((n) => {
            const active = current === n.href
            return (
              <Link
                key={n.href}
                href={n.href}
                lang="zh-Hant"
                aria-current={active ? "page" : undefined}
                className="relative text-[13px] tracking-[0.18em] transition-colors duration-300 focus-visible:outline-none"
                style={{
                  fontFamily: "var(--proto-mono)",
                  color: active ? paper.ink : paper.inkFaint,
                }}
              >
                {n.zh}
                <span
                  className="absolute -bottom-1.5 left-0 h-px w-full origin-left transition-transform duration-300"
                  style={{ background: paper.accent, transform: active ? "scaleX(1)" : "scaleX(0)" }}
                  aria-hidden
                />
              </Link>
            )
          })}
        </nav>
        <a
          href={`mailto:${identity.email}`}
          lang="zh-Hant"
          className="rounded-full px-4 py-2 text-[12px] tracking-wide transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          style={{
            fontFamily: "var(--proto-mono)",
            border: `1px solid ${paper.hairline}`,
            color: paper.inkSoft,
          }}
        >
          {/* #35 (v3)：header 膠囊用短版；CtaBlock 與 hero 維持全句 */}
          {cta.primaryShort.zh}
        </a>
      </motion.div>
    </header>
  )
}

// The page's single CTA moment (§2.0) — paper register, vermilion primary.
export function CtaBlock({
  headline,
  sub,
}: {
  headline?: { zh: string; en?: string }
  sub?: { zh: string; en?: string }
}) {
  const h = headline ?? cta.primary
  return (
    <section aria-label="聯絡" className="relative overflow-hidden px-6 py-24 md:px-10 md:py-36">
      <div className="relative mx-auto max-w-5xl text-center">
        <FadeUp>
          {/* H14 (v3)：中文欄位放中文，en 原字串作次行 */}
          <Eyebrow zh={identity.availabilityZh} en={identity.availability} />
        </FadeUp>
        <FadeUp delay={0.07}>
          <h2
            lang="zh-Hant"
            className={`mx-auto mt-6 max-w-4xl text-balance ${type.h1.cls}`}
            style={{ fontFamily: ZH_SERIF, fontVariationSettings: type.h1.vs, color: paper.ink }}
          >
            {h.zh}
          </h2>
          {h.en && (
            <p
              className={`mx-auto mt-5 italic ${type.caption.cls}`}
              style={{ fontFamily: "var(--proto-fraunces)", color: paper.inkFaint }}
            >
              {h.en}
            </p>
          )}
          {sub && (
            <p
              lang="zh-Hant"
              className={`mx-auto mt-5 max-w-2xl ${type.body.cls}`}
              style={{ fontFamily: "var(--proto-hanken)", color: paper.inkSoft }}
            >
              {sub.zh}
            </p>
          )}
          {sub?.en && (
            <p
              className={`mx-auto mt-3 max-w-2xl italic ${type.caption.cls}`}
              style={{ fontFamily: "var(--proto-fraunces)", color: paper.inkFaint }}
            >
              {sub.en}
            </p>
          )}
        </FadeUp>
        <FadeUp delay={0.14}>
          <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href={`mailto:${identity.email}`}
              className="group inline-flex items-center gap-3 rounded-full px-8 py-4 text-[15px] font-medium transition-transform duration-300 hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
              style={{ background: paper.accent, color: paper.bg, fontFamily: "var(--proto-hanken)" }}
            >
              {identity.email}
              <span aria-hidden className="transition-transform duration-300 group-hover:translate-x-1">
                →
              </span>
            </a>
            <a
              href={identity.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-[15px] transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
              style={{
                border: `1px solid ${paper.hairline}`,
                color: paper.inkSoft,
                fontFamily: "var(--proto-hanken)",
              }}
            >
              LinkedIn
            </a>
          </div>
        </FadeUp>
      </div>
    </section>
  )
}

export function SiteFooter() {
  return (
    <footer className="px-6 py-10 md:px-10" style={{ borderTop: `1px solid ${paper.hairlineSoft}` }}>
      <div
        className="mx-auto flex max-w-7xl flex-col items-center gap-2 text-center text-[12px] tracking-[0.14em]"
        style={{ fontFamily: "var(--proto-mono)", color: paper.inkFaint }}
      >
        <span lang="zh-Hant">{footerLine.zh}</span>
        <span>{footerLine.en}</span>
        <span>{identity.location.toUpperCase()}</span>
      </div>
    </footer>
  )
}

// Page shell — the paper canvas. Grain kept at whisper level (restrained
// finishing layer, not the identity gesture — jury-fatigue note, round 2 §5).
export function PageShell({
  children,
  fontVars,
  current,
}: {
  children: ReactNode
  fontVars: string
  current?: string
}) {
  return (
    <div
      className={`${fontVars} relative min-h-screen overflow-x-clip antialiased`}
      style={{ background: paper.bg, color: paper.ink }}
    >
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.05] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
        aria-hidden
      />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-6 focus:top-6 focus:z-50 focus:rounded-full focus:px-5 focus:py-2 focus:text-sm"
        style={{ background: paper.accent, color: paper.bg }}
      >
        跳到內容
      </a>
      <SiteHeader current={current} />
      <main id="main" className="relative z-10 pt-16">
        {children}
      </main>
      <SiteFooter />
    </div>
  )
}

// InstrumentRoom — the dark inset where analysis happens (方向 C).
// Wrap diagnostic instruments, metric walls, ledgers. Never narrative prose.
export function InstrumentRoom({
  children,
  className = "",
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-2xl ${className}`}
      style={{
        background: "linear-gradient(to bottom, #16130d, #12100C)",
        border: "1px solid #2C2820",
        boxShadow: "0 24px 60px -30px rgba(31,27,20,0.45)",
      }}
    >
      {children}
    </div>
  )
}
